import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slider, RadioGroup, Radio, Divider, Switch, InputGroup } from '@blueprintjs/core';
import TeachRTooltip from "../../components/Tooltip/TeachRTooltip"
import './ConfigurationStyle.css';

import {
    setNumStudents,
    setDisorderLevel,
    setStartingSituation,
    setInterventionControl,
    setNoiseLevel,
    setLogFileName,
    setEnableAllScripts,
    setUseSpeechTools,
    selectNumStudents,
    selectDisorderLevel,
    selectStartingSituation,
    selectInterventionControl,
    selectNoiseLevel,
    selectLogFileName,
    selectEnableAllScripts,
    selectUseSpeechTools
} from './ConfigurationOptionsSlice';

import { setWeather, selectWeather } from '../SessionSettings/WeatherSlice'

/**
 * ConfigureOptions is a React component that renders various configuration options 
 * for managing class settings, atmosphere, event logging, and speech tools.
 *
 * It allows users to:
 * - Set the number of students.
 * - Adjust the level of disorder.
 * - Choose the starting situation.
 * - Choose an intervention type for disruptions.
 * - Modify weather conditions.
 * - Set the noise level.
 * - Configure event logging options.
 * - Enable or disable recording functionality.
 *
 * @returns {JSX.Element} - A form-like interface with configuration options for the classroom session.
 */
const ConfigureOptions = () => {
    const dispatch = useDispatch();

    // Select the values from the Redux store
    const numStudents = useSelector(selectNumStudents);
    const disorderLevel = useSelector(selectDisorderLevel);
    const startingSituationPresest = useSelector(selectStartingSituation);
    const interventionControl = useSelector(selectInterventionControl);

    // Fallback for invalid values
    const safeNumStudents = Number.isFinite(numStudents) ? numStudents : 1;
    const safeDisorderLevel = Number.isFinite(disorderLevel) ? disorderLevel : 0;

    const weather = useSelector(selectWeather);
    const noiseLevel = useSelector(selectNoiseLevel);
    const logFileName = useSelector(selectLogFileName);
    const enableAllScripts = useSelector(selectEnableAllScripts);
    const useSpeechTools = useSelector(selectUseSpeechTools);

    // helper functions to convert boolean into strings:
    const handleInterventionSwitch = (checked) => {
        dispatch(setInterventionControl(checked ? 'hybrid' : 'manually'));
    };

    const handleStartingSituationSwitch = (checked) => {
        dispatch(setStartingSituation(checked ? 'attentionForward' : 'seatwork'));
    };

    return (
        <div className="config-options-container">
            {/* CLASS SETTINGS */}
            <h6 className="custom-heading">Class Settings</h6>
            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Number of students</label>
                    <TeachRTooltip
                        content="Amount of students in the classroom"
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <div className="custom-slider">
                        <Slider
                            min={0}
                            max={30}
                            stepSize={1}
                            labelStepSize={5}
                            value={safeNumStudents}
                            onChange={(value) => dispatch(setNumStudents(value))}
                        />
                    </div>
                </div>
            </div>

            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Level of disorder:</label>
                    <TeachRTooltip
                        content=
                        <div>
                            <p>An increasing level represents a raising chance to missbehave on each student.</p>
                            <ul>
                                <li>0: absolute disciplin, class behaviour soleley derived by coach</li>
                                <li>1: rare automatic occurence of misbehavior</li>
                                <li>2: occasional automatic occurence of misbehavior</li>
                                <li>3: chaotic class</li>
                            </ul>
                        </div>
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <div className="custom-slider">
                        <Slider
                            min={0}
                            max={3}
                            stepSize={1}
                            labelStepSize={1}
                            value={safeDisorderLevel}
                            onChange={(value) => dispatch(setDisorderLevel(value))}
                        />
                    </div>
                </div>
            </div>

            <div className="config-section">
                <label className="label-container">
                    <label className="custom-label">Starting situation:</label>
                    <div className="switch-with-labels">
                        <TeachRTooltip
                            content="In the beginning of the lesson, students can work in their notebooks (seat work) or look at the teacher (attention forward)."
                            icon="info-sign"
                            iconColor="var(--highlight_1)"
                            tooltipDirection="right" />
                        <span>Seatwork</span>
                        <Switch
                            checked={startingSituationPresest === 'attentionForward'}
                            onChange={(e) => handleStartingSituationSwitch(e.target.checked)}
                            className="custom-switch" />
                        <span style={{ alignItems: "flex-start" }}>Attention forward</span>
                    </div>
                </label>
            </div>

            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Intervention control:</label>
                    <TeachRTooltip
                        content="Just coach can change behavior vs. proximity of teacher intervents misbehavior"
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <div className="switch-with-labels">
                        <span>manually</span>
                        <Switch
                            checked={interventionControl === 'hybrid'}
                            onChange={(e) => handleInterventionSwitch(e.target.checked)}
                            className="custom-switch"
                        />
                        <span>hybrid</span>
                    </div>
                </div>
            </div>

            <Divider />

            {/* ATMOSPHERE */}
            <h6 className="custom-heading">Atmosphere</h6>

            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Weather:</label>
                    <TeachRTooltip
                        content="Weather to start the classroom with."
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <RadioGroup
                        onChange={(e) => dispatch(setWeather(e.target.value))}
                        selectedValue={weather}
                        inline={true}
                    >
                        <Radio label="Sunny" value="sunny" />
                        <Radio label="Raining" value="raining" />
                        <Radio label="Snowing" value="snowing" />
                    </RadioGroup>
                </div>
            </div>

            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Noise Level:</label>
                    <TeachRTooltip
                        content="Starting level of background noise in the classroom"
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <div className="custom-slider">
                        <Slider
                            min={0} // { 0.15f, 0.5f, 0.7f, 0.8f, 1.0f };
                            max={4}
                            stepSize={1}
                            labelStepSize={1}
                            value={noiseLevel}
                            onChange={(value) => dispatch(setNoiseLevel(value))}
                            labelRenderer={(val) => Math.round(val)}
                        />
                    </div>
                </div>
            </div>

            <Divider />

            {/* EVENT LOGGER */}
            <h6 className="custom-heading">Data collection</h6>
            <p className="custom-explanation">Data can be found in: StreamingAssets/Measurements/</p>
            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Start data collection:</label>
                    <TeachRTooltip
                        content=
                        <div>
                            <p> With <u>on connection</u>, the data collection will start as early as possible and for every available script.</p>
                            <p> With <u>manually</u> the data collection will not start automatically on connection.
                                Instead, you will have to select the scripts you want the data to be collected from manually when unity and the website share a connection. </p>
                            <p>You can track </p>
                            <ul>
                                <li>student behavior</li>
                                <li>students in field of view</li>
                                <li>students focused (eye tracking necessary)</li>
                                <li>heartbeat (Polar H10 necessary)</li>
                                <li>teacher movement (tracked by HMD)</li>
                                <li>hand movement (tracked by controller position)</li>
                                <li>nodes in theme tree</li>
                                <li>weather</li>
                            </ul>
                            <p>You can deselect (and re-select) a script at any time.</p>
                        </div>
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right"
                    />
                    <span>Manually</span>
                    <Switch
                        checked={enableAllScripts}
                        onChange={(e) => dispatch(setEnableAllScripts(e.target.checked))}
                        className="custom-switch"
                    />
                    <span>On connection</span>
                </div>

            </div>

            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Directory name:</label>
                    <TeachRTooltip
                        content="Optional. Directory name will be extended by date and time to prevent accidental overwriting."
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <InputGroup
                        placeholder="Enter log file name"
                        value={logFileName}
                        onChange={(e) => dispatch(setLogFileName(e.target.value))}
                    />
                </div>
            </div>

            <Divider />

            {/* SPEECH TOOLS */}
            <h6 className="custom-heading">Speechtools</h6>

            <div className="config-section">
                <div className="label-container">
                    <label className="custom-label">Speech analysis:</label>
                    <TeachRTooltip
                        content="Collecting speech data can be manually started after connection is established with VR or will be started immediately with connection."
                        icon="info-sign"
                        iconColor="var(--highlight_1)"
                        tooltipDirection="right" />
                    <span>Manually</span>
                    <Switch
                        checked={useSpeechTools}
                        onChange={(e) => dispatch(setUseSpeechTools(e.target.checked))}
                        className="custom-switch"
                    />
                    <span>On connection</span>
                </div>
            </div>

            <Divider />
        </div>
    );
};

export default ConfigureOptions;
